import { useContext, createContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useOutlet, useSearchParams } from "react-router-dom";

import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'


import api from '../API.js';
import { purgeFailedApiQueries } from '../utils/apiResponse.js';

// (emailauth) React now routes through the API landing page, so we no longer
// consume tokens directly here.

const AuthContext = createContext();
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnMount: false, // Don't refetch on mount if data is fresh
    },
  },
});

export { queryClient };

// Inner component that uses React Query hooks (must be inside QueryClientProvider)
const AuthProviderInner = ({ outlet, girderToken, emailAuthToken, clearEmailAuthTokenFromUrl }) => {
  const [token, setTokenState] = useState(girderToken || localStorage.girderToken);
  const queryClient = useQueryClient();
  const wasServerPendingRef = useRef(true);

  // Readiness probe first — gate all other Girder-backed queries on this.
  // Non-200 (e.g. nginx 502 while Girder boots) and fetch errors stay "pending"
  // so ConfigGuard shows waiting UI instead of false "needs setup".
  const { data: localNetworkData } = useQuery({
    queryKey: ['local-info'],
    queryFn: () => api.get('network/local'),
    retry: 2,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => (query.state.data?.status === 200 ? false : 2000),
  });

  const girderInfo = localNetworkData?.status === 200 && localNetworkData?.data
    ? localNetworkData.data
    : 'pending';
  const serverReady = girderInfo !== 'pending';

  // Discard caches poisoned by boot-time 502s when the server first becomes ready.
  useEffect(() => {
    if (serverReady && wasServerPendingRef.current) {
      purgeFailedApiQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ['user-me'] });
      queryClient.invalidateQueries({ queryKey: ['oauth-providers'] });
      queryClient.invalidateQueries({ queryKey: ['emailauth-capabilities'] });
      queryClient.invalidateQueries({ queryKey: ['local-connections'] });
    }
    wasServerPendingRef.current = !serverReady;
  }, [serverReady, queryClient]);

  const { data: oauthData } = useQuery({
    queryKey: ['oauth-providers'],
    queryFn: () => api.get('oauth/provider', {query: {redirect: window.location.href, list: true}}),
    enabled: serverReady,
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const oauth = useMemo(() => {
    return oauthData?.status === 200 && oauthData?.data ? oauthData.data : [];
  }, [oauthData]);

  const { data: emailAuthCapsData } = useQuery({
    queryKey: ['emailauth-capabilities'],
    queryFn: () => api.get('emailauth/capabilities'),
    enabled: serverReady,
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const emailAuth = useMemo(() => {
    if (emailAuthCapsData?.status === 200 && emailAuthCapsData?.data) {
      const d = emailAuthCapsData.data;
      return {
        oneTimeEmailLogin: !!d.oneTimeEmailLogin,
        emailFromHint: typeof d.emailFromHint === 'string' ? d.emailFromHint : '',
      };
    }
    return null;
  }, [emailAuthCapsData]);

  const { data: userData, isLoading: isLoadingUser, isFetching: isFetchingUser } = useQuery({
    queryKey: ['user-me'],
    queryFn: () => api.get('user/me'),
    enabled: !!token && serverReady,
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const user = useMemo(() => {
    return userData?.status === 200 && userData?.data ? userData.data : null;
  }, [userData]);

  // Keep auth pending while waiting for Girder or post-ready user/me so we don't
  // flash LoginWidget after a boot-time failure or across the wait→ready gap.
  const pending = !!token && (
    !serverReady ||
    isLoadingUser ||
    isFetchingUser ||
    (serverReady && userData === undefined)
  );

  const setToken = (t) => {
    console.log('Setting localStorage.girderToken', t)
    t ? localStorage.setItem('girderToken',t) : localStorage.removeItem('girderToken');
    setTokenState(t);
  }


  // Any time localStorage changes, invalidate user query to trigger refetch
  useEffect(() => {
      const onStorageChange = ()=>{
        // Local storage may have changed, but if the girderToken is the same as our existing one, don't refresh
        if(localStorage.girderToken === token){
          return;
        }
        // Ok, new girderToken, invalidate user query to trigger refetch
        queryClient.invalidateQueries({ queryKey: ['user-me'] });
      }
      window.addEventListener('storage', onStorageChange);
      
      // cleanup by removing the listener on unmount
      return ()=> window.removeEventListener('storage', onStorageChange);
          
  }, [token, queryClient])

  // Invalidate connections query when token changes from null/undefined to a value (login)
  useEffect(() => {
    // Only invalidate when token transitions from falsy to truthy (login scenario)
    if (token) {
      queryClient.invalidateQueries({ queryKey: ['local-connections'] });
    }
  }, [token, queryClient]);

  /**
   * onLoginResponse
   * @param {Response} resp 
   * @returns status: true if login succeeded, false otherwise.
   */
  const onLoginResponse = (resp) => {
    if(resp.data && resp.data.authToken){
      // Update React Query cache with user data from login response
      queryClient.setQueryData(['user-me'], { status: 200, data: resp.data.user });
      setToken(resp.data.authToken.token);
      return true;
    } else {
      // Clear user data on failed login
      queryClient.setQueryData(['user-me'], null);
      setToken(null);
      return false;
    }
  }
  
  const onLogoutResponse = () => {
    // Clear user data from React Query cache
    queryClient.setQueryData(['user-me'], null);
    // Invalidate queries to ensure all components re-render
    queryClient.invalidateQueries({ queryKey: ['user-me'] });
    setToken(null);
  }




  const doLogin = async (username, password)=>{
    const headers = {
      Authorization: `Basic ${btoa(username + ":" + password)}`,
    }
    return api.get('/user/authentication', {headers}).then(onLoginResponse);
  }
  const doLogout = async ()=>{
    return api.delete('/user/authentication').then(onLogoutResponse);
  }

  // Canonical email-auth token bootstrap (streamlined):
  // If React sees emailAuthToken in the URL, bounce through the API landing page which
  // performs the consume + localStorage.girderToken + redirect in one place.
  useEffect(() => {
    if (!emailAuthToken) return;
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('emailAuthToken');
      const redirect = u.toString();
      const landing = api.url('emailauth/auth/landing', {
        query: { token: emailAuthToken, redirect }
      });
      window.location.assign(landing);
    } catch {
      // If URL parsing fails, fall back to home.
      window.location.assign(api.url('emailauth/auth/landing', {
        query: { token: emailAuthToken, redirect: '/' }
      }));
    }
  }, [emailAuthToken]);

  const refreshBdsaInfo = async () => {
    // Invalidate React Query cache to trigger refetch
    await queryClient.invalidateQueries({ queryKey: ['local-info'] });
    // Return the updated data from cache
    const data = queryClient.getQueryData(['local-info']);
    return data?.status === 200 && data?.data ? data.data : null;
  }

  return (
    <AuthContext.Provider value={{user, oauth, emailAuth, token, pending, girderInfo, doLogin, doLogout, refreshBdsaInfo}}>
      {outlet}
    </AuthContext.Provider>
  );
};

const AuthProvider = () => {
  const outlet = useOutlet();
  const [searchParams, setSearchParams] = useSearchParams();
  const girderToken = searchParams.get('girderToken');
  const emailAuthToken = searchParams.get('emailAuthToken');

  const clearEmailAuthTokenFromUrl = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!next.has('emailAuthToken')) {
          return prev;
        }
        next.delete('emailAuthToken');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  if(girderToken){
      console.log('setting localStorage.girderToken from query',girderToken)
      localStorage.setItem('girderToken', girderToken);
    }
  useEffect(()=>{
    if(girderToken){
      searchParams.delete('girderToken');
      setSearchParams(searchParams);
    }
  }, [girderToken])

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProviderInner
        outlet={outlet}
        girderToken={girderToken}
        emailAuthToken={emailAuthToken}
        clearEmailAuthTokenFromUrl={clearEmailAuthTokenFromUrl}
      />
    </QueryClientProvider>
  );
};

export default AuthProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};
